import { useMemo, useState, useCallback } from 'react';
import { ModalContent, ModalFooter } from '@components/modal';
import TransferOwnershipModal from '@pages/common/modals/TransferOwnershipConfirmation';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '@src/api/queryClient';
import { AUTHORIZED_USER_QUERY_KEYS, AuthorizedUserMutations } from '@src/api/services/authorized-user';
import { useModal } from '@stores';
import { useUserProfile } from '@hooks';
import RemoveUserConfirmationModal from './RemoveUserConfirmation';
import UserSearch from '@components/user-search/UserSearch';
import { useUserSearch } from '@api/services/profile/hooks/useUserSearch';
import { UserPermissions, ResponseStatuses } from '@src/utils/enums';
import { toast } from 'react-toastify';
import InternalLink from '@components/internal-link/InternalLink';
import { PermissionsInfoAlert } from '../components/PermissionsInfoAlert';
import LabelValue from '@components/label-value/LabelValue';
import { Alert } from '@trussworks/react-uswds';
import useGetAuthorizedUsers from '@api/services/authorized-user/hooks/useGetAuthorizedUsers';
import AuthorizedUsersTable from '@components/manage-access/AuthorizedUsersTable';
import Tabs from '@components/tabs/Tabs';
import { DASHBOARD_QUERY_KEYS } from '@src/api/services/dashboard';

const omitKey = (obj, keyToOmit) => {
  const { [keyToOmit]: omitted, ...rest } = obj;
  void omitted;
  return rest;
};

const RequestAccessModal = ({ projectName, requestType, requestId, version }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [apiErrors, setApiErrors] = useState();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingPermissionChanges, setPendingPermissionChanges] = useState({});
  const [pendingDeactivations, setPendingDeactivations] = useState([]);
  const [pendingTransferOwnership, setPendingTransferOwnership] = useState(null);
  const [activeAccessTab, setActiveAccessTab] = useState(0);
  const [userIssueMap, setUserIssueMap] = useState({});

  const { openSecondaryModal, closeModal } = useModal();
  const { data: users = [], isFetching, error: searchError } = useUserSearch(searchQuery);
  const { userProfileData } = useUserProfile();
  const currentUserEmail = userProfileData?.emailAddress?.toLowerCase();
  const { data: authorizedUsersDataRaw = [] } = useGetAuthorizedUsers({ requestId, version });

  const projectOwner = authorizedUsersDataRaw.find((user) => user.permission === UserPermissions.Owner);

  //@TODO TEMPORARY, REMOVE THIS IN RRS-3053
  const ownershipEligibleUserIds = useMemo(
    () =>
      new Set(
        authorizedUsersDataRaw
          .filter((user) => user.authorizedUserID !== 0 && user.endValid === null)
          .map((user) => user.authorizedUserID)
      ),
    [authorizedUsersDataRaw]
  );

  const normalizeTransferErrors = (transfer) => {
    if (!transfer) return [];
    if (Array.isArray(transfer)) return transfer;
    if (typeof transfer === 'object') return Object.values(transfer).filter(Boolean);
    return [];
  };

  const getIssueKey = (errorItem) => {
    if (errorItem?.authorizedUserID) return 'existing_' + errorItem.authorizedUserID;
    if (errorItem?.userID) return 'new_' + errorItem.userID;
    return null;
  };

  const buildIssueState = (dataObj, fallbackMessage) => {
    const transferErrors = normalizeTransferErrors(dataObj?.transfer);
    const allErrors = [
      ...(dataObj?.add ?? []),
      ...(dataObj?.update ?? []),
      ...(dataObj?.deactivate ?? []),
      ...transferErrors,
    ].filter(Boolean);

    const issueMap = {};
    const errors = allErrors.map((e) => {
      const message = '[' + (e?.errorCode ?? 'UNKNOWN') + '] ' + (e?.message ?? 'Unknown error');
      const issueKey = getIssueKey(e);
      if (issueKey) issueMap[issueKey] = message;
      return message;
    });

    return {
      errors: errors.length ? errors : [fallbackMessage ?? 'Some updates could not be completed.'],
      issueMap,
    };
  };

  const { mutate: bulkUpdateAuthorizedUsers, isPending: isSaving } = useMutation({
    mutationFn: AuthorizedUserMutations.bulkUpdateAuthorizedUsers,
    onSuccess: (response) => {
      const status = response?.status;
      const msg = response?.msg;
      const partialData = response?.data ?? {};

      const resetPendingStateAndClose = () => {
        setApiErrors(null);
        queryClient.invalidateQueries({ queryKey: [AUTHORIZED_USER_QUERY_KEYS.GET_AUTHORIZED_USER] });
        setPendingUsers([]);
        setPendingPermissionChanges({});
        setPendingDeactivations([]);
        setPendingTransferOwnership(null);
        setUserIssueMap({});
        closeModal();
      };

      switch (status) {
        case ResponseStatuses.Success: {
          toast.success(msg ?? 'Successfully updated authorized users.');
          resetPendingStateAndClose();
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          return;
        }

        case ResponseStatuses.PartialSuccess: {
          const issueState = buildIssueState(partialData, msg);
          setApiErrors(issueState.errors);
          setUserIssueMap(issueState.issueMap);
          toast.warn(msg ?? 'Some updates were completed, some failed.');
          queryClient.invalidateQueries({ queryKey: [AUTHORIZED_USER_QUERY_KEYS.GET_AUTHORIZED_USER] });
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          return;
        }

        case ResponseStatuses.Warning: {
          const warningData = response?.data ?? {};
          const issueState = buildIssueState(warningData, msg);
          const hasRowIssues = Object.keys(issueState.issueMap).length > 0;

          setApiErrors(issueState.errors);
          setUserIssueMap(hasRowIssues ? issueState.issueMap : {});
          toast.warn(msg ?? 'Request completed with warnings.');
          queryClient.invalidateQueries({ queryKey: [AUTHORIZED_USER_QUERY_KEYS.GET_AUTHORIZED_USER] });
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          return;
        }

        case ResponseStatuses.Failed: {
          setUserIssueMap({});
          setApiErrors(msg ? [msg] : ['Failed to update authorized users.']);
          toast.error(msg ?? 'Failed to update authorized users.');
          return;
        }

        default: {
          // Backward compatibility in case API still returns old shape without status
          const legacyHasPartialErrors =
            response?.add?.length > 0 ||
            response?.update?.length > 0 ||
            response?.deactivate?.length > 0 ||
            Object.keys(response?.transfer ?? {}).length > 0;

          if (legacyHasPartialErrors) {
            const issueState = buildIssueState(response, msg);
            setApiErrors(issueState.errors);
            setUserIssueMap(issueState.issueMap);
            queryClient.invalidateQueries({ queryKey: [AUTHORIZED_USER_QUERY_KEYS.GET_AUTHORIZED_USER] });
          } else {
            toast.success('Successfully updated authorized users.');
            resetPendingStateAndClose();
          }
        }
      }
    },
    onError: (error) => {
      setApiErrors(null);
      setUserIssueMap({});
      toast.error(error.message ?? 'Failed to update authorized users.');
    },
  });

  const authorizedUsersData = useMemo(() => {
    const deactivated = new Set(pendingDeactivations);

    return [...authorizedUsersDataRaw, ...pendingUsers]
      .filter((user) => !deactivated.has(user.authorizedUserID))
      .map((user) => ({
        ...user,
        permission:
          pendingPermissionChanges[user.authorizedUserID !== 0 ? user.authorizedUserID : `new_${user.userID}`] ??
          user.permission,
      }))
      .sort((a, b) => {
        const aIsCurrent = a.email?.toLowerCase() === currentUserEmail;
        const bIsCurrent = b.email?.toLowerCase() === currentUserEmail;
        if (aIsCurrent) return -1;
        if (bIsCurrent) return 1;
        return 0;
      });
  }, [authorizedUsersDataRaw, currentUserEmail, pendingDeactivations, pendingPermissionChanges, pendingUsers]);

  const currentUserData = authorizedUsersDataRaw.find((user) => user.email?.toLowerCase() === currentUserEmail);
  const isCurrentUserOwner = currentUserData?.permission === UserPermissions.Owner;

  const currentUsers = useMemo(() => {
    const filtered = authorizedUsersData.filter((user) => user.endValid === null);
    if (isCurrentUserOwner) {
      return filtered.filter((user) => {
        const userIdKey = user.authorizedUserID === 0 ? `new_${user.userID}` : String(user.authorizedUserID);
        const hasPendingOwnerChange = pendingPermissionChanges[userIdKey] === UserPermissions.Owner;

        return (
          user.permission !== UserPermissions.Owner ||
          user.authorizedUserID === pendingTransferOwnership ||
          hasPendingOwnerChange
        );
      });
    }
    return filtered;
  }, [authorizedUsersData, isCurrentUserOwner, pendingTransferOwnership, pendingPermissionChanges]);

  const accessHistory = useMemo(
    () => authorizedUsersDataRaw.filter((user) => user.endValid !== null),
    [authorizedUsersDataRaw]
  );

  const searchErrorMessage = searchError?.response?.data?.msg || null;

  const handleRemoveUser = useCallback(
    (user) => {
      openSecondaryModal(RemoveUserConfirmationModal, {
        user,
        onConfirm: () => {
          if (user.authorizedUserID === 0) {
            setPendingUsers((current) => current.filter((u) => u.userID !== user.userID));
            return;
          }

          setPendingDeactivations((current) => {
            if (current.includes(user.authorizedUserID)) return current;
            return [...current, user.authorizedUserID];
          });

          setPendingPermissionChanges((current) => omitKey(current, String(user.authorizedUserID)));

          setPendingTransferOwnership((current) => (current === user.authorizedUserID ? null : current));
        },
      });
    },
    [openSecondaryModal]
  );

  const handlePermissionsChange = useCallback(
    (user, newRole) => {
      if (!user || newRole === user.permission) return;

      // Use userID for new users (authorizedUserID === 0), authorizedUserID for existing users
      const userIdKey = user.authorizedUserID === 0 ? `new_${user.userID}` : String(user.authorizedUserID);

      if (newRole === UserPermissions.Owner) {
        setPendingPermissionChanges((current) => ({
          ...current,
          [userIdKey]: UserPermissions.Owner,
        }));

        openSecondaryModal(TransferOwnershipModal, {
          user,
          onConfirm: () => {
            setPendingTransferOwnership(user.authorizedUserID);
          },
          onCancel: () => {
            setPendingPermissionChanges((current) => omitKey(current, userIdKey));
            setPendingTransferOwnership(null);
          },
        });
        return;
      }

      setPendingTransferOwnership((current) => (current === user.authorizedUserID ? null : current));
      setPendingPermissionChanges((current) => {
        if (newRole === user.permission) {
          return omitKey(current, userIdKey);
        }

        return {
          ...current,
          [userIdKey]: newRole,
        };
      });
    },
    [openSecondaryModal]
  );

  const handleUserSelect = useCallback(
    (user) => {
      if (!user) return;

      const selectedEmail = user.emailAddress?.trim().toLowerCase();
      if (!selectedEmail) return;

      const alreadyPresent = authorizedUsersData.some(
        (existingUser) => existingUser.email?.trim().toLowerCase() === selectedEmail && existingUser.endValid === null
      );

      if (alreadyPresent) {
        const duplicateUser = authorizedUsersData.find(
          (existingUser) => existingUser.email?.trim().toLowerCase() === selectedEmail && existingUser.endValid === null
        );

        if (duplicateUser) {
          const issueKey =
            duplicateUser.authorizedUserID !== 0
              ? 'existing_' + duplicateUser.authorizedUserID
              : 'new_' + duplicateUser.userID;

          setUserIssueMap((current) => ({
            ...current,
            [issueKey]: '[DUPLICATE_USER] User is already added to this request.',
          }));
        }

        setApiErrors(['[DUPLICATE_USER] User is already added to this request.']);
        setSearchQuery('');
        toast.warn('User ' + user.emailAddress + ' has already been added to this request.');
        return;
      }

      const existingUser = authorizedUsersData?.[0];

      const payload = {
        authorizedUserID: 0,
        projectID: existingUser?.projectID ?? 0,
        projectVersion: existingUser?.projectVersion ?? 0,
        requestID: requestId,
        version: version,
        userID: user.userID,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.emailAddress,
        role: user.role,
        permission: UserPermissions.ReadOnly,
        beginValid: null,
        endValid: null,
      };

      setPendingUsers((current) => [...current, payload]);
      setSearchQuery('');
    },
    [authorizedUsersData, requestId, version]
  );

  const tabData = useMemo(
    () => [
      {
        title: 'Current Users',
        content: (
          <div className='margin-top-neg-1'>
            <div className='usa-hint font-ui-2xs margin-bottom-1' id='current-users-hint'>
              View and manage users who currently have access to this request.
            </div>

            <AuthorizedUsersTable
              users={currentUsers}
              variant='current'
              onRemoveUser={handleRemoveUser}
              onPermissionsChange={handlePermissionsChange}
              userIssueMap={userIssueMap}
              ownershipEligibleUserIds={ownershipEligibleUserIds} //@TODO TEMPORARY, REMOVE THIS IN RRS-3053
            />
          </div>
        ),
      },
      {
        title: 'Access History',
        content: (
          <div className='margin-top-neg-1'>
            <div className='usa-hint font-ui-2xs margin-bottom-1' id='access-history-hint'>
              View users whose access to this request has ended.
            </div>

            <AuthorizedUsersTable users={accessHistory} variant='history' />
          </div>
        ),
      },
    ],
    [accessHistory, currentUsers, handleRemoveUser, handlePermissionsChange, ownershipEligibleUserIds, userIssueMap]
  );

  const hasPendingChanges =
    pendingUsers.length > 0 ||
    Object.keys(pendingPermissionChanges).length > 0 ||
    pendingDeactivations.length > 0 ||
    pendingTransferOwnership !== null;

  const handleSave = () => {
    const deactivatedSet = new Set(pendingDeactivations);

    const add = pendingUsers
      .filter((user) => !deactivatedSet.has(user.authorizedUserID))
      .map((user) => {
        const userIdKey = `new_${user.userID}`;
        const changedPermission = pendingPermissionChanges[userIdKey];
        return {
          userID: user.userID,
          permission: changedPermission ?? user.permission,
        };
      });

    const update = Object.entries(pendingPermissionChanges)
      .filter(([key]) => !key.startsWith('new_'))
      .map(([authorizedUserID, permission]) => ({
        authorizedUserID: Number(authorizedUserID),
        permission,
      }))
      .filter((item) => !deactivatedSet.has(item.authorizedUserID))
      .filter((item) => item.authorizedUserID !== pendingTransferOwnership)
      .filter((item) => item.authorizedUserID !== 0);

    const payload = {
      add,
      update,
      deactivate: pendingDeactivations,
      transferOwnership: pendingTransferOwnership || 0,
    };

    const isEmptyPayload =
      payload.add.length === 0 &&
      payload.update.length === 0 &&
      payload.deactivate.length === 0 &&
      payload.transferOwnership === 0;

    if (isEmptyPayload) return;

    bulkUpdateAuthorizedUsers({ requestId, version, payload });
  };

  const handleCancel = () => {
    closeModal();
  };

  return (
    <ModalContent title='Modify Request Access'>
      <section className='modal-body'>
        {apiErrors?.length ? (
          <Alert type='warning' heading='Unable to complete some access updates' headingLevel='h4'>
            <ul className='margin-top-1 margin-bottom-0'>
              {apiErrors.map((errorMessage, index) => (
                <li key={index}>{errorMessage}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div className='pn-info-container'>
          <section className='pn-section flex-item'>
            <h3>Details</h3>
            <div className='grid-row grid-gap'>
              <div className='grid-col-12 tablet:grid-col-6'>
                <div className='pn-flex-item'>
                  <LabelValue label='Project Name' value={projectName} />
                </div>
                <LabelValue label='Request Type' value={requestType} />
              </div>
              <div className='grid-col-12 tablet:grid-col-6'>
                <div className='pn-flex-item'>
                  <LabelValue
                    label='Project Owner'
                    value={`${projectOwner?.firstName ?? ''} ${projectOwner?.lastName ?? ''}`.trim()}
                  />
                </div>
                <LabelValue
                  label='Project Owner Email'
                  value={
                    <InternalLink
                      content={projectOwner?.email}
                      title={`MailTo ${projectOwner?.email}`}
                      href={`mailto:${projectOwner?.email}`}
                    />
                  }
                />
              </div>
            </div>
          </section>

          <section className='pn-section flex-item'>
            <h3>Add User to Request</h3>
            <div className='pn-flex-container'>
              <div className='pn-flex-item'>
                <label className='usa-label' id='user-search-input'>
                  <b>User Search:</b>
                  <div className='usa-hint font-ui-2xs padding-top-1' id='user-search-hint'>
                    <span>
                      Search by email to add a user to this request. The user must have an existing RRS account before
                      being added.
                    </span>
                  </div>
                </label>
                <UserSearch
                  users={users}
                  isLoading={isFetching}
                  onUserSelect={handleUserSelect}
                  onSearchInputChange={setSearchQuery}
                  inputValue={searchQuery}
                  errorMessage={searchErrorMessage}
                />
              </div>
            </div>
          </section>

          <section className='pn-section'>
            <h3>Manage Request Access</h3>
            <Tabs tabData={tabData} activeIndex={activeAccessTab} onTabChange={setActiveAccessTab} />
          </section>

          <section className='pn-section margin-top-2'>
            <PermissionsInfoAlert />
          </section>
        </div>
      </section>

      <ModalFooter
        customClosingLogic={hasPendingChanges}
        onCancel={handleCancel}
        saveIsDisabled={!hasPendingChanges || isSaving}
        onSave={handleSave}
        saveText='Save'
      />
    </ModalContent>
  );
};

export default RequestAccessModal;
